import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Restore,
  Star,
  StarBorder,
  AccountTree,
  LocationOn,
  Business,
  Search,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import { useRoleDefinitions } from '../../hooks/useRoleDefinitions';
import { usePeopleAdmin } from '../../context/PeopleAdminContext';
import { fetchHierarchyDefinitions } from '../../services/hierarchyService';

const columnHelper = createColumnHelper();

/**
 * Component for displaying role definitions in a hierarchical table
 * @param {Object} props - Component props
 * @param {Function} props.onEdit - Callback when edit is clicked
 * @param {Function} props.onViewUsage - Callback to view role usage
 * @returns {React.Component} Role definition list table
 */
function RoleDefinitionList({ onEdit, onViewUsage }) {
  const {
    roleDefinitions,
    deactivateRole,
    reactivateRole,
    setDefaultRole,
    isDeactivating,
    isReactivating,
    isSettingDefault,
    getSortedRoles
  } = useRoleDefinitions();
  const { clientId } = usePeopleAdmin();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [sorting, setSorting] = useState([{ id: 'assignmentLevel', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [hierarchyLabels, setHierarchyLabels] = useState({});

  useEffect(() => {
    if (clientId) {
      fetchHierarchyDefinitions(clientId)
        .then(levels => {
          const labels = {
            'LOCATION_LEVEL': 'Location'
          };
          levels.forEach(level => {
            if (['ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'].includes(level.levelId)) {
              labels[level.levelId] = level.label;
            }
          });
          setHierarchyLabels(labels);
        })
        .catch(error => {
          console.error('Failed to load hierarchy labels:', error);
          // Use defaults
          setHierarchyLabels({
            'LOCATION_LEVEL': 'Location',
            'ORGLEVEL_1': 'District',
            'ORGLEVEL_2': 'Region',
            'ORGLEVEL_3': 'Division'
          });
        });
    }
  }, [clientId]);

  const handleMenuOpen = (event, role) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
  };

  const handleEdit = () => {
    if (selectedRole && onEdit) {
      onEdit(selectedRole);
    }
    handleMenuClose();
  };

  const handleToggleActive = async () => {
    if (selectedRole) {
      try {
        if (selectedRole.isActive) {
          await deactivateRole(selectedRole.id);
        } else {
          await reactivateRole(selectedRole.id);
        }
      } catch (error) {
        console.error('Failed to toggle role status:', error);
      }
    }
    handleMenuClose();
  };

  const handleSetDefault = async () => {
    if (selectedRole) {
      try {
        await setDefaultRole(selectedRole.id);
      } catch (error) {
        console.error('Failed to set default role:', error);
      }
    }
    handleMenuClose();
  };

  const handleViewUsage = () => {
    if (selectedRole && onViewUsage) {
      onViewUsage(selectedRole);
    }
    handleMenuClose();
  };

  const getAssignmentLevelIcon = (assignmentLevel) => {
    if (assignmentLevel === 'LOCATION_LEVEL') return <LocationOn fontSize="small" />;
    return <Business fontSize="small" />;
  };

  const getAssignmentLevelLabel = (assignmentLevel) => {
    return hierarchyLabels[assignmentLevel] || assignmentLevel || 'Unknown';
  };

  const getRoleCategoryLabel = (category, assignmentLevel) => {
    // If no category is set, return appropriate message
    if (!category) {
      return assignmentLevel === 'LOCATION_LEVEL' ? 'Not Set' : 'N/A';
    }
    
    // Map category values to display labels
    switch (category) {
      case 'FOH':
        return 'Front of House';
      case 'BOH':
        return 'Back of House';
      case 'MANAGEMENT':
        return 'Management';
      case 'OTHER':
        return 'Other';
      default:
        return category || 'Not Set';
    }
  };

  // Get sorted roles - will update when roleDefinitions changes
  const sortedRoles = useMemo(() => {
    console.log('RoleDefinitionList - Recalculating sortedRoles, roleDefinitions count:', roleDefinitions.length);
    return getSortedRoles();
  }, [getSortedRoles, roleDefinitions]);

  // Define columns for TanStack Table
  const columns = useMemo(
    () => [
      columnHelper.accessor('assignmentLevel', {
        header: 'Assignment Level',
        cell: (info) => {
          const assignmentLevel = info.getValue();
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              {getAssignmentLevelIcon(assignmentLevel)}
              <Typography variant="body2" fontWeight="medium">
                {getAssignmentLevelLabel(assignmentLevel)}
              </Typography>
            </Stack>
          );
        },
        sortingFn: 'basic',
      }),
      columnHelper.accessor('roleName', {
        header: 'Role Name',
        cell: (info) => (
          <Typography variant="body2" fontWeight="medium">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('roleCode', {
        header: 'Role Code',
        cell: (info) => (
          <Typography variant="body2" fontFamily="monospace">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('category', {
        header: 'Category',
        cell: (info) => {
          const category = info.getValue();
          const assignmentLevel = info.row.original.assignmentLevel;
          const hasCategory = Boolean(category);
          return (
            <Typography 
              variant="body2" 
              color={!hasCategory && assignmentLevel !== 'LOCATION_LEVEL' ? 'text.disabled' : 'text.primary'}
            >
              {getRoleCategoryLabel(category, assignmentLevel)}
            </Typography>
          );
        },
      }),
      columnHelper.accessor('isActive', {
        header: () => <Box textAlign="center">Status</Box>,
        cell: (info) => (
          <Box textAlign="center">
            <Chip
              label={info.getValue() !== false ? 'Active' : 'Inactive'}
              color={info.getValue() !== false ? 'success' : 'error'}
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: '22px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
          </Box>
        ),
      }),
      columnHelper.accessor('isDefault', {
        header: () => <Box textAlign="center">Default</Box>,
        cell: (info) => (
          <Box textAlign="center">
            <Tooltip title={info.getValue() ? 'Default Role' : 'Not Default'}>
              <span>
                <IconButton size="small" disabled>
                  {info.getValue() ? (
                    <Star color="warning" fontSize="small" />
                  ) : (
                    <StarBorder fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      }),
      columnHelper.accessor('assignmentCount', {
        header: () => <Box textAlign="center">Usage</Box>,
        cell: (info) => (
          <Box textAlign="center">
            <Tooltip title="View usage details">
              <Typography
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onClick={() => onViewUsage && onViewUsage(info.row.original)}
              >
                {info.getValue() || 0}
              </Typography>
            </Tooltip>
          </Box>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <Box textAlign="right">Actions</Box>,
        cell: ({ row }) => (
          <Box textAlign="right">
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, row.original)}
              disabled={isDeactivating || isReactivating || isSettingDefault}
            >
              <MoreVert />
            </IconButton>
          </Box>
        ),
      }),
    ],
    [isDeactivating, isReactivating, isSettingDefault, onViewUsage, hierarchyLabels]
  );

  // Create table instance
  const table = useReactTable({
    data: sortedRoles,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  
  // Log when table data changes
  useEffect(() => {
    console.log('RoleDefinitionList - Table data updated, row count:', sortedRoles.length);
    if (sortedRoles.length > 0) {
      console.log('RoleDefinitionList - Sample role:', sortedRoles[0]);
    }
  }, [sortedRoles]);

  if (!sortedRoles || sortedRoles.length === 0) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography variant="body1" color="text.secondary" align="center">
          No role definitions found. Create your first role to get started.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Search Bar */}
      <Box mb={2}>
        <TextField
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search roles..."
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }} key={`table-${roleDefinitions.length}-${JSON.stringify(roleDefinitions[0]?.lastModified || '')}`}>
        <Box sx={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.875rem',
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        padding: '16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        borderBottom: '2px solid #e0e0e0',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: 'relative',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <Box sx={{ ml: 'auto' }}>
                            {{
                              asc: <ArrowUpward fontSize="small" />,
                              desc: <ArrowDownward fontSize="small" />,
                            }[header.column.getIsSorted()] ?? (
                              <Box sx={{ width: 20 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    opacity: row.original.isActive === false ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0',
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Role</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleToggleActive}>
          <ListItemIcon>
            {selectedRole?.isActive ? (
              <Delete fontSize="small" />
            ) : (
              <Restore fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedRole?.isActive ? 'Deactivate' : 'Reactivate'}
          </ListItemText>
        </MenuItem>

        {selectedRole?.isActive && !selectedRole?.isDefault && (
          <MenuItem onClick={handleSetDefault}>
            <ListItemIcon>
              <Star fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set as Default</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleViewUsage}>
          <ListItemIcon>
            <AccountTree fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Usage</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

RoleDefinitionList.propTypes = {
  onEdit: PropTypes.func,
  onViewUsage: PropTypes.func
};

export default RoleDefinitionList;